import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  People, 
  Groups, 
  Business, 
  Event, 
  ManageAccounts,
  CorporateFare,
  GroupAdd,
  EventNote,
  Campaign,
  ArrowForward,
  Download,
  Edit as EditIcon,
  FindInPage as LostFoundIcon,
  Lightbulb as SkillsIcon,
  Help as DoubtsIcon,
  Settings as SettingsIcon,
  Dashboard as DashboardIcon
} from '@mui/icons-material';
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { useSnackbar } from 'notistack';
import LogoutNavbar from '../../components/LogoutNavbar';
import GlobalSidebar from '../../components/GlobalSidebar';
import PostLoginFooter from '../../components/PostLoginFooter';
import AnnouncementBanner from '../../components/AnnouncementBanner';
import '../../styles/AdminDashboard.css';
import api from '../../api';
import { getFullImageUrl } from '../../middleware/Imagegetter';

import logoImage from '../../assets/images/Logo.svg';
import naProfile from '../../assets/images/NA.jpg';

/** Small helper: reveal-on-scroll for any .reveal element */
function useRevealOnScroll() {
  useEffect(() => {
    const els = Array.from(document.querySelectorAll('.reveal'));
    if (!els.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('reveal-visible');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

/** Count-up animation when stats section enters the viewport */
function useStatCounters(count) {
  const startedRef = useRef(false);

  useEffect(() => {
    startedRef.current = false;

    const section = document.querySelector('.stats-section');
    if (!section) return;

    const numbers = Array.from(section.querySelectorAll('.stat-number'));

    const animate = (el, target) => {
      const duration = 1200;
      const start = performance.now();
      const startVal = 0;

      const step = (now) => {
        const p = Math.min(1, (now - start) / duration);
        const val = Math.floor(startVal + (target - startVal) * p);
        el.textContent = val.toLocaleString();
        if (p < 1) requestAnimationFrame(step);
      };

      requestAnimationFrame(step);
    };

    const io = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry.isIntersecting || startedRef.current) return;
        startedRef.current = true;

        numbers.forEach((n) => {
          const target = parseInt(n.getAttribute('data-target') || '0', 10);
          animate(n, target);
        });
      },
      { threshold: 0.25 }
    );

    io.observe(section);
    return () => io.disconnect();
  }, [count]);
}

const AdminDashboard = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { enqueueSnackbar } = useSnackbar();
  const footerRef = useRef(null);
  const fileInputRef = useRef(null);

  const [count, setcount] = useState({
    username: "",
    college_name: "",
    student_count: 0,
    club_member_count: 0,
    college_admin_count: 0,
    club_count: 0,
    event_count: 0
  });

  const [adminProfile, setAdminProfile] = useState({
    name: "",
    username: "",
    firstName: "",
    lastName: "",
    designation: "Admin",
    image: "",
  });

  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [previewImage, setPreviewImage] = useState("");
  const [imageVersion, setImageVersion] = useState(0);

  const resolveProfileImageSrc = (rawImage) => {
    if (previewImage) return previewImage;
    const full = getFullImageUrl(rawImage);
    if (!full) return naProfile;
    const sep = full.includes('?') ? '&' : '?';
    return `${full}${sep}v=${imageVersion}`;
  };

  useEffect(() => {
    return () => {
      if (previewImage && String(previewImage).startsWith('blob:')) {
        try { URL.revokeObjectURL(previewImage); } catch { /* ignore */ }
      }
    };
  }, [previewImage]);

  // Define complete sidebar items for both desktop and mobile
  const sidebarItems = [
    // Dashboard & Profile
    { 
      label: 'Dashboard', 
      path: '/admin/dashboard', 
      action: () => navigate('/admin/dashboard'),
      icon: <DashboardIcon />
    },
    // { 
    //   label: 'Profile', 
    //   path: '/admin/profile', 
    //   action: () => navigate('/admin/profile'),
    //   icon: <ProfileIcon />
    // },
    
    // Management Sections (from student portal)
    { 
      label: 'Lost and Found', 
      path: '/lostfound', 
      action: () => navigate('/lostfound'),
      icon: <LostFoundIcon />
    },
    { 
      label: 'Skills', 
      path: '/skills', 
      action: () => navigate('/skills'),
      icon: <SkillsIcon />
    },
    { 
      label: 'Doubts', 
      path: '/yourdoubts', 
      action: () => navigate('/yourdoubts'),
      icon: <DoubtsIcon />
    },
    
    // Admin Management
    { 
      label: 'Manage Students', 
      path: '/admin/students', 
      action: () => navigate('/admin/students'),
      icon: <ManageAccounts />
    },
    { 
      label: 'Manage Clubs', 
      path: '/admin/clubs', 
      action: () => navigate('/admin/clubs'),
      icon: <CorporateFare />
    },
    { 
      label: 'Club Membership', 
      path: '/admin/memberships', 
      action: () => navigate('/admin/memberships'),
      icon: <GroupAdd />
    },
    // { 
    //   label: 'College Management', 
    //   path: '/admin/college', 
    //   action: () => navigate('/admin/college'),
    //   icon: <CollegeIcon />
    // },
    
    // Events & Announcements
    { 
      label: 'Event Management', 
      path: '/eventmanagement', 
      action: () => navigate('/eventmanagement'),
      icon: <EventNote />
    },
    
    // System & Analytics
    // { 
    //   label: 'Analytics', 
    //   path: '/admin/analytics', 
    //   action: () => navigate('/admin/analytics'),
    //   icon: <AnalyticsIcon />
    // },
    // { 
    //   label: 'Admin Settings', 
    //   path: '/admin/settings', 
    //   action: () => navigate('/admin/settings'),
    //   icon: <AdminIcon />
    // },
    // { 
    //   label: 'Security', 
    //   path: '/admin/security', 
    //   action: () => navigate('/admin/security'),
    //   icon: <SecurityIcon />
    // },
    { 
      label: 'Settings', 
      path: '/settings', 
      action: () => navigate('/settings'),
      icon: <SettingsIcon />
    },
  ];

  useEffect(() => {
    const fetchcollegestats = async () => {
      try {
        const [statsResponse, meResponse] = await Promise.all([
          api.get('users/college-stats/'),
          api.get('users/me/'),
        ]);

        setcount({
          username: statsResponse.username,
          college_name: statsResponse.college_name,
          student_count: statsResponse.student_count,
          club_member_count: statsResponse.club_member_count,
          college_admin_count: statsResponse.college_admin_count,
          club_count: statsResponse.club_count,
          event_count: statsResponse.event_count
        });

        const me = meResponse?.user || meResponse;
        const role = meResponse?.role || meResponse?.user?.role || me?.role;
        const first = String(me?.first_name || "").trim();
        const last = String(me?.last_name || "").trim();
        const username = String(me?.username || "").trim();
        const displayName = ([first, last].filter(Boolean).join(' ') || username || 'Admin').trim();

        const designation = (() => {
          const r = String(role || '').toLowerCase();
          if (r === 'college_admin') return 'College Admin';
          if (r === 'admin') return 'Admin';
          if (r) return r.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
          return 'Admin';
        })();

        setAdminProfile({
          name: displayName,
          username,
          firstName: first,
          lastName: last,
          designation,
          image: me?.image || "",
        });
      } catch (e) {
        // Keep dashboard usable even if one endpoint fails
        console.error('Failed to load admin dashboard data:', e);
      }
    }
    fetchcollegestats();
  }, []);

  useRevealOnScroll();
  useStatCounters(count);

  const adminName = count.username || 'Admin';
  const collegeName = count.college_name || 'Your College';

  const adminDisplayName = adminProfile.name || adminName;

  const clearPhotoSelection = () => {
    setPhotoFile(null);
    if (previewImage && String(previewImage).startsWith('blob:')) {
      try { URL.revokeObjectURL(previewImage); } catch { /* ignore */ }
    }
    setPreviewImage('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const beginEditProfile = () => {
    if (isSavingProfile) return;
    setEditProfileOpen(false);
    clearPhotoSelection();
    fileInputRef.current?.click();
  };

  const closeEditProfile = () => {
    setEditProfileOpen(false);
    setIsSavingProfile(false);
    clearPhotoSelection();
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (previewImage && String(previewImage).startsWith('blob:')) {
      try { URL.revokeObjectURL(previewImage); } catch { /* ignore */ }
    }

    setPhotoFile(file);
    const url = URL.createObjectURL(file);
    setPreviewImage(url);
    setEditProfileOpen(true);
  };

  const handleSaveProfile = async () => {
    try {
      setIsSavingProfile(true);
      const file = photoFile || fileInputRef.current?.files?.[0];
      if (!file) {
        enqueueSnackbar('Please choose an image first.', { variant: 'error' });
        return;
      }

      const formData = new FormData();
      formData.append('image', file);

      const response = await api.post('users/upload-photo/', formData);
      const nextImage = response?.image || response?.user?.image || '';

      setAdminProfile((prev) => ({
        ...prev,
        image: nextImage || prev.image,
      }));
      setImageVersion((v) => v + 1);
      enqueueSnackbar('Profile photo updated successfully!', { variant: 'success' });
      closeEditProfile();
    } catch (error) {
      console.error('Profile update failed:', error);
      const msg =
        error?.response?.data?.error ||
        (Array.isArray(error?.response?.data?.details) ? error.response.data.details.join('\n') : null) ||
        error?.message ||
        'Profile update failed. Please try again.';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setIsSavingProfile(false);
    }
  };

  const statsData = [
    { 
      title: 'Total Students', 
      count: count.student_count,
      icon: <People />,
      variant: 'students',
      change: '+12%',
      changeType: 'positive',
      subtitle: 'Active learners',
      bgColor: '#667eea15'
    },
    { 
      title: 'Club Members', 
      count: count.club_member_count,
      icon: <Groups />,
      variant: 'members',
      change: '+8%',
      changeType: 'positive',
      subtitle: 'Engaged members',
      bgColor: '#f5576c15'
    },
    { 
      title: 'Active Clubs', 
      count: count.club_count,
      icon: <Business />,
      variant: 'clubs',
      change: '+3%',
      changeType: 'positive',
      subtitle: 'Communities',
      bgColor: '#4facfe15'
    },
    { 
      title: 'Total Events', 
      count: count.event_count,
      icon: <Event />,
      variant: 'events',
      change: '+15%',
      changeType: 'positive',
      subtitle: 'This month',
      bgColor: '#43e97b15'
    },
  ];

  const quickActions = [
    { 
      title: 'Manage Students', 
      icon: <ManageAccounts />,
      description: 'View, edit, and manage student profiles',
      color: '#6366f1',
      path: '/admin/students',
      category: 'Management'
    },
    { 
      title: 'Manage Clubs', 
      icon: <CorporateFare />,
      description: 'Oversee club creation and activities',
      color: '#1A9D5A',
      path: '/admin/clubs',
      category: 'Management'
    },
    { 
      title: 'Club Membership', 
      icon: <GroupAdd />,
      description: 'Handle club membership and roles',
      color: '#2563eb',
      path: '/admin/memberships',
      category: 'Management'
    },
    { 
      title: 'Event Management', 
      icon: <EventNote />,
      description: 'Create and moderate campus events',
      color: '#f59e0b',
      path: '/eventmanagement',
      category: 'Events'
    },
  ];

  const handleQuickAction = (action) => {
    if (action.path) {
      navigate(action.path);
    }
  };

  const handleExportData = () => {
    try {
      const payload = {
        generatedAt: new Date().toISOString(),
        admin: {
          name: adminDisplayName,
          username: adminProfile.username || adminName,
          designation: adminProfile.designation || 'Admin',
        },
        college: collegeName,
        stats: {
          student_count: count.student_count,
          club_member_count: count.club_member_count,
          club_count: count.club_count,
          event_count: count.event_count,
        },
      };

      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `edunexus-admin-dashboard-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      enqueueSnackbar('Export started (JSON).', { variant: 'success' });
    } catch (e) {
      enqueueSnackbar('Export failed. Please try again.', { variant: 'error' });
    }
  };

  const handleNewAnnouncement = () => {
    navigate('/admin/announcements?new=1');
  };

  return (
    <div className="admin-dashboard" style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Pass sidebarItems to Navbar for mobile view */}
      <LogoutNavbar sidebarItems={isMobile ? sidebarItems : []} />

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handlePhotoSelect}
      />
      
      <div className="dashboard-layout" style={{ display: 'flex', flexGrow: 1 }}>
        {/* Only show sidebar on desktop */}
        {!isMobile && <GlobalSidebar />}
        
        <main className="dashboard-main" style={{ flexGrow: 1, overflowX: 'hidden' }}>
          <div className="dashboard-container">

            <header className="dashboard-header reveal" aria-label="Admin dashboard header">
              <div className="header-content" style={{ alignItems: 'stretch' }}>
                <div className="admin-details-card admin-details-card--header" aria-label="Admin details">
                  <div className="admin-details-actions" aria-label="Header actions">
                    <button type="button" className="btn-secondary" onClick={handleExportData}>
                      <Download className="btn-icon-sm" />
                      Export
                    </button>
                    <button type="button" className="btn-primary" onClick={handleNewAnnouncement}>
                      <Campaign className="btn-icon" />
                      New Announcement
                    </button>
                  </div>

                  <div className="admin-details-avatar">
                    <div className="admin-avatar-circle" aria-hidden="true">
                      <img
                        className="admin-details-avatar-img"
                        src={resolveProfileImageSrc(adminProfile.image)}
                        alt=""
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = naProfile;
                        }}
                      />
                    </div>

                    <IconButton
                      size="small"
                      onClick={beginEditProfile}
                      disableRipple
                      disableFocusRipple
                      className="admin-avatar-edit-btn"
                      aria-label="Edit profile"
                    >
                      <EditIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </div>

                  <div className="admin-details-meta">
                    <div className="admin-details-name">{adminDisplayName}</div>
                    <div className="admin-details-row">
                      <div className="admin-details-label">Designation</div>
                      <div className="admin-details-value">{adminProfile.designation || 'Admin'}</div>
                    </div>
                    <div className="admin-details-row">
                      <div className="admin-details-label">College</div>
                      <div className="admin-details-value">{collegeName}</div>
                    </div>
                  </div>
                </div>
              </div>
            </header>

            <Dialog
              open={editProfileOpen}
              onClose={closeEditProfile}
              fullWidth
              maxWidth="sm"
              aria-label="Change photo dialog"
            >
              <DialogTitle>Change Photo</DialogTitle>
              <DialogContent dividers>
                <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0' }}>
                  <div
                    className="admin-details-avatar"
                    aria-hidden="true"
                    style={{ width: 96, height: 96, cursor: 'pointer' }}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <img
                      className="admin-details-avatar-img"
                      src={resolveProfileImageSrc(adminProfile.image)}
                      alt=""
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = naProfile;
                      }}
                    />
                  </div>
                </div>
              </DialogContent>
              <DialogActions>
                <button type="button" className="btn-secondary" onClick={closeEditProfile} disabled={isSavingProfile}>
                  Cancel
                </button>
                <button type="button" className="btn-primary" onClick={handleSaveProfile} disabled={isSavingProfile || !photoFile}>
                  {isSavingProfile ? 'Saving...' : 'Save'}
                </button>
              </DialogActions>
            </Dialog>

            <div style={{ margin: '12px 0 18px' }}>
              <AnnouncementBanner />
            </div>

            {/* Branded College Hero + Stats */}
            <section className="college-hero reveal" aria-label="College overview">
              <div className="college-hero-inner">
                <img className="college-hero-watermark" src={logoImage} alt="" aria-hidden="true" />
                <div className="college-hero-content">
                  <h2 className="college-hero-title">{collegeName}</h2>
                  <p className="college-hero-subtitle">Snapshot of platform activity and growth.</p>
                </div>

                <div className="stats-section" style={{ marginBottom: 0 }}>
                  <div className="container">
                    <div className="stats-grid">
                      {statsData.map((s) => (
                        <div key={s.title} className={`stat-card ${s.variant || ''} reveal`}>
                          <div className="stat-body" style={{ alignItems: 'center' }}>
                            <div className="stat-icon">{s.icon}</div>
                            <div className="stat-number" data-target={s.count}>0</div>
                            <div className="stat-label">{s.title}</div>
                            <div className="stat-subtitle">{s.subtitle}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Enhanced Quick Actions */}
            <section className="actions-section">
              <div className="section-header">
                <h2 className="section-title">Quick Actions</h2>
                <p className="section-subtitle">Streamline your administrative tasks</p>
              </div>
              <div className="actions-grid">
                {quickActions.map((action, index) => (
                  <article
                    key={index}
                    className="action-card"
                    onClick={() => handleQuickAction(action)}
                  >
                    <div className="action-card-header">
                      <div 
                        className="action-icon"
                        style={{ 
                          backgroundColor: `${action.color}15`,
                          color: action.color 
                        }}
                      >
                        {action.icon}
                      </div>
                      <div className="action-meta">
                        {action.badge && (
                          <span className="action-badge">{action.badge}</span>
                        )}
                        <span className="action-category">{action.category}</span>
                      </div>
                    </div>
                    <div className="action-body">
                      <h3 className="action-title">{action.title}</h3>
                      <p className="action-description">{action.description}</p>
                    </div>
                    <div className="action-footer">
                      <ArrowForward className="action-arrow" />
                    </div>
                  </article>
                ))}
              </div>
            </section>

            {/* Dashboard Grid Layout */}
            <div className="dashboard-grid">
              {/* Removed: Recent Activity, Pending Approvals, System Status sections */}
            </div>

          </div>
        </main>
      </div>

      {/* Footer with ref for Sidebar auto-hide functionality */}
      <div ref={footerRef}>
        <PostLoginFooter />
      </div>
    </div>
  );
};

export default AdminDashboard;